/**
 * TaskPreviewDialog — 캘린더에서 Task 마감일 chip 클릭 시 띄우는 경량 미리보기.
 *
 * Task 는 본질적으로 게시글(post) 이라 풀 상세는 게시판 모듈의 PostDetailOverlay 가 정답.
 * 여기선 캘린더 컨텍스트에 맞춰 핵심 정보만:
 *  - 채널명 + 상태 badge
 *  - title
 *  - 담당자 / 마감일 + D-N
 *  - [닫기] [게시판에서 보기] — 후자는 TaskEmitter.emit('open', roomId, postId)
 *
 * 메인 캘린더에서 다른 채널 task 를 클릭한 경우: 그 채널 page 로 navigate 후 emit.
 * 채널 캘린더에서 같은 채널 task 를 클릭한 경우: 그냥 emit (이미 그 채널 페이지).
 */
import React from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Hash, User, Calendar as CalendarIcon, ExternalLink } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { CalendarEvent } from '../../models/CalendarEvent';
import { TASK_STATUS_CONFIG, TaskStatus, getTaskStatusLabel } from '../../models/Board';
import { TaskEmitter } from '../tasks/TaskEmitter';
import { useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { cn } from '../../lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: CalendarEvent | null;
}

function daysUntil(iso: string): { label: string; tone: 'past' | 'today' | 'soon' | 'future' } {
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (24 * 3600 * 1000));
  if (diff < 0) return { label: t`${Math.abs(diff)} days overdue`, tone: 'past' };
  if (diff === 0) return { label: t`Today`, tone: 'today' };
  if (diff <= 3) return { label: `D-${diff}`, tone: 'soon' };
  return { label: `D-${diff}`, tone: 'future' };
}

const TaskPreviewDialog: React.FC<Props> = ({ open, onOpenChange, event }) => {
  const history = useHistory();
  const { rooms } = useAppUIContext();

  if (!event) return null;
  const meta = event.metadata ?? {};
  const channelId = meta.channel_id as string | undefined;
  const postId = meta.post_id as string | undefined;
  const status = (meta.task_status ?? 'requested') as TaskStatus;
  const assignee = meta.assignee as { uid: string; name: string; photoUrl?: string | null } | null;
  const channel = rooms.find((r) => r.id === channelId);
  const channelName = channel?.name ?? t`(channel)`;
  const due = daysUntil(event.start_at);
  const dueLabel = new Date(event.start_at).toLocaleDateString(i18n.locale || 'en', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });
  const statusConfig = TASK_STATUS_CONFIG[status];

  const openInBoard = () => {
    if (!channelId || !postId) return;
    onOpenChange(false);
    if (window.location.pathname !== `/channel/${channelId}`) {
      history.push(`/channel/${channelId}`);
      // RoomPage listener 마운트 대기 후 emit (마이크로태스크 라운드 트립)
      setTimeout(() => TaskEmitter.emit('open', channelId, postId), 250);
    } else {
      TaskEmitter.emit('open', channelId, postId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-sky-500" />
            <Trans>Task deadline</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">
          {/* 채널 + 상태 */}
          <div className="flex items-center gap-2 text-meta">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Hash className="size-3.5" />
              {channelName}
            </span>
            <span className={cn(
              'px-1.5 py-0.5 rounded text-meta font-medium',
              statusConfig.bgColor,
              statusConfig.color,
            )}>
              {getTaskStatusLabel(status)}
            </span>
          </div>

          {/* 제목 */}
          <h3 className="text-body-lg font-semibold text-foreground">{event.title}</h3>

          {/* 마감 + 담당자 */}
          <div className="flex flex-col gap-2 pt-2 border-t border-border/40">
            <div className="flex items-center gap-2 text-label">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="text-foreground">{dueLabel}</span>
              <span className={cn(
                'ml-auto px-2 py-0.5 rounded-md text-meta font-medium tabular-nums',
                due.tone === 'past' && 'bg-destructive/10 text-destructive',
                due.tone === 'today' && 'bg-badge-caution-bg text-badge-caution',
                due.tone === 'soon' && 'bg-badge-warn-bg text-badge-warn',
                due.tone === 'future' && 'bg-muted text-muted-foreground',
              )}>
                {due.label}
              </span>
            </div>
            <div className="flex items-center gap-2 text-label">
              <User className="size-4 text-muted-foreground" />
              {assignee ? (
                <div className="flex items-center gap-1.5">
                  <UserAvatar src={assignee.photoUrl ?? undefined} name={assignee.name} className="size-5 shrink-0" />
                  <span className="text-foreground">{assignee.name}</span>
                </div>
              ) : (
                <span className="text-muted-foreground"><Trans>No assignee</Trans></span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            <Trans>Close</Trans>
          </Button>
          <Button size="sm" onClick={openInBoard} disabled={!channelId || !postId}>
            <ExternalLink className="size-3.5 mr-1.5" />
            <Trans>View in board</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskPreviewDialog;
